import React, { useState } from 'react';
import './styles';
import { useSelector } from 'react-redux';
import UIListInputRow from '../../UI/UIListInputRow';
import UIIconButton from '../../UI/UIIconButton';
import { CloseIcon } from '../../../assets/icons';
import { USER_ROLE_AFFILIATE, USER_ROLE_AGENT, USER_ROLE_PHOTOGRAPHER } from '../../../utils/Config/Constants';
import * as EmailValidator from 'email-validator';
import Queries from '../../../utils/API/Queries';
import UIOverlayCard from '../../UI/UIOverlayCard';
import { UI_LABELED_INPUT_DROPDOWN_TYPE, UI_LABELED_INPUT_PHONE_NUMBER_TYPE, UI_LABELED_INPUT_TEXT_TYPE } from '../../UI/UILabeledInput/config';
import { DropdownItem } from '../../UI/UILabeledInput/types';
import { useMutation } from '@apollo/react-hooks';
import { toast } from 'react-toastify';
import { getSignedInUserSelector } from '../../../redux/selectors/CurrentUserSelectors';
import * as strings from './strings';
import { UI_ICON_BUTTON_FILL_TYPE } from '../../UI/UIIconButton/config';

export default function CreateUserModal({ role, hideModal }: CreateUserModalProps) {
	const signedInUser = useSelector(getSignedInUserSelector);

	const [updateService] = useMutation(Queries.CREATE_USER_MUTATION, {
		onError: (error) => {
			setIsLoading(false);
			toast.error(error.message.replace('GraphQL error: ', '').replace(/(^|\s)\S/g, (letter) => letter.toUpperCase()));
		}
	});

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [userToCreateName, setUserToCreateName] = useState<string>('');
	const [userToCreateEmail, setUserToCreateEmail] = useState<string>('');
	const [userToCreatePhoneNumber, setUserToCreatePhoneNumber] = useState<string>('');
	const [userToCreatePassword, setUserToCreatePassword] = useState<string>('');
	const [selectedRegionsArray, setSelectedRegionsArray] = useState<RegionType[]>([]);

	const canCreateUser = () => {
		return (
			userToCreateName.trim().length > 0 &&
			EmailValidator.validate(userToCreateEmail.trim()) &&
			userToCreatePhoneNumber.trim().length > 0 &&
			userToCreatePassword.trim().length > 0
		);
	};

	const createUser = async () => {
		if (!isLoading) {
			if (userToCreateName.trim().length === 0) {
				toast.info(strings.userNameError);
			} else if (!EmailValidator.validate(userToCreateEmail.trim())) {
				toast.info(strings.userEmailError);
			} else if (userToCreatePhoneNumber.trim().length === 0) {
				toast.info(strings.userEmailValidError);
			} else if (userToCreatePassword.trim().length === 0) {
				toast.info(strings.userPasswordError);
			} else {
				setIsLoading(true);
				const response = await updateService({
					variables: {
						role: role,
						name: userToCreateName,
						email: userToCreateEmail,
						phoneNumber: userToCreatePhoneNumber,
						password: userToCreatePassword,
						certificates: [],
						regions:
							role === USER_ROLE_AGENT || role === USER_ROLE_AFFILIATE
								? null
								: selectedRegionsArray.map((region) => {
										return region.id;
								  }),
						requiresPasswordChange: true
					}
				});
				const { signup } = response['data'];
				if (signup) {
					toast.success(strings.successUserMessage(signup.name));
					setIsLoading(false);
					hideModal();
				} else {
					setIsLoading(false);
					toast.error(strings.errorUserCreation);
				}
			}
		}
	};

	const deleteRegion = (regionToDelete) => {
		let newDataArray = selectedRegionsArray.filter((region) => region.id !== regionToDelete.id);
		for (let i = 0; i < selectedRegionsArray.length; i++) {
			if (selectedRegionsArray[i] === regionToDelete) {
				selectedRegionsArray.splice(i, 1);
			}
		}
		setSelectedRegionsArray(newDataArray);
	};

	const renderUserCreateInputs = () => {
		let optionsDropdown: DropdownItem[] = [];
		if ([USER_ROLE_AFFILIATE, USER_ROLE_AGENT, USER_ROLE_PHOTOGRAPHER].indexOf(role) !== -1) {
			for (let index = 0; index < signedInUser.regions.length; index++) {
				let region = signedInUser.regions[index];
				if (selectedRegionsArray.indexOf(region) === -1) {
					optionsDropdown.push({ value: region, label: region.name });
				}
			}
		}
		return (
			<div>
				<UIListInputRow
					label={strings.nameLabel}
					type={UI_LABELED_INPUT_TEXT_TYPE}
					onChangeValue={setUserToCreateName}
					value={userToCreateName}
					placeholder={strings.namePlaceholder}
				/>
				<UIListInputRow
					label={strings.emailLabel}
					type={UI_LABELED_INPUT_TEXT_TYPE}
					onChangeValue={setUserToCreateEmail}
					value={userToCreateEmail}
					placeholder={strings.emailPlaceholder}
				/>
				<UIListInputRow
					label={strings.phoneNumberLabel}
					type={UI_LABELED_INPUT_PHONE_NUMBER_TYPE}
					onChangeValue={setUserToCreatePhoneNumber}
					value={userToCreatePhoneNumber}
					placeholder={strings.phoneNumberPlaceholder}
				/>
				<UIListInputRow
					label={strings.passwordLabel}
					type={UI_LABELED_INPUT_TEXT_TYPE}
					onChangeValue={setUserToCreatePassword}
					value={userToCreatePassword}
					placeholder={strings.passwordPlaceholder}
				/>
				{role === USER_ROLE_AGENT || role === USER_ROLE_AFFILIATE ? undefined : (
					<div>
						<UIListInputRow
							containerStyle={{ marginTop: 16, width: 320 }}
							type={UI_LABELED_INPUT_DROPDOWN_TYPE}
							options={optionsDropdown}
							onChangeDropdownValue={(value) => setSelectedRegionsArray([...selectedRegionsArray, value.value])}
							placeholder={strings.addRegionPlaceholder}
						/>
						<p className='label_input_label' style={{ marginTop: '16px' }}>
							{strings.selectedRegionTitle}
						</p>
						{selectedRegionsArray.length > 0 ? (
							selectedRegionsArray.map((region) => {
								return (
									<div key={region.id} className='createUserModal__region'>
										<p>{region.name}</p>
										<img
											key={region.id}
											alt={strings.closeIconAlt}
											className='createUserModal__region--remove'
											src={CloseIcon.white}
											onClick={() => deleteRegion(region)}
										/>
									</div>
								);
							})
						) : (
							<p>{strings.noRegionsWarning}</p>
						)}
					</div>
				)}
				<UIIconButton
					dark
					isDisabled={!canCreateUser()}
					isLoading={isLoading}
					disabledErrorMessage={strings.disabledButtonWarning}
					type={UI_ICON_BUTTON_FILL_TYPE}
					text={strings.createButtonText}
					onClick={createUser}
				/>
			</div>
		);
	};

	return (
		<UIOverlayCard hideModal={hideModal} title={strings.titleWithRole(role)}>
			{renderUserCreateInputs()}
		</UIOverlayCard>
	);
}
